import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { push } from 'redux-first-history';
import { CustomTable, MobileTable, SimpleLoader } from '@dev/ee-components';

import { getLandingPageList, LoadLandingPagesListStart } from '@/store/landingPageList/actions';
import { PreviewLandingPage } from '@/components/LandingPageList/components/Preview';
import { PreviewContent } from '@/components/LandingPageList/LandingPageListInterface';
import useMobile from '@/services/hooks/useMobile';
import { LandingPagePaths } from '@/services/dictionaries/Paths';

import { EmptyStateContainer, MobileTableContainer, TableContainer } from './styles';
import { EmptyState } from '../EmptyState';
import { columnsWidth, defaultParams } from './config';
import { useTableHeaders } from './hooks/useTableHeaders';
import { useTableRows } from './hooks/useTableRows';
import { useMobileTableRows } from './hooks/useMobileTableRows';
import { useMobileTableHeaders } from './hooks/useMobileTableHeaders';


export const LinkInBioWidgetContent = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isMobile = useMobile();
    const [openPreview, setOpenPreview] = useState(false);
    const [previewContent, setPreviewContent] = useState<PreviewContent>(null);
    const { PreviewLoading, name, template, landingPagesList, loadingLandingPagesList } = useSelector(getLandingPageList);

    const headers = useTableHeaders();
    const rows = useTableRows(landingPagesList, setOpenPreview);
    const mobileHeaders = useMobileTableHeaders();
    const mobileRows = useMobileTableRows(landingPagesList);

    useEffect(() => {
        dispatch(LoadLandingPagesListStart(defaultParams.type, defaultParams.offset, defaultParams.limit));
    }, []);

    useEffect(() => {
        if (PreviewLoading) {
            return;
        }
        setPreviewContent({
            name,
            content: template as string,
        });
    },[PreviewLoading]);

    const onPreviewClose = (): void => {
        setPreviewContent(null);
        setOpenPreview(false);
    };

    if (loadingLandingPagesList) {
        return <SimpleLoader />;
    }

    if (landingPagesList.length === 0) {
        return (
            <EmptyStateContainer $isMobile={isMobile}>
                <EmptyState
                    title={t('buildalinkinbio')}
                    description={t('createcustomizablepage')}
                    button={{
                        text: t('createlinkinbio'),
                        onClick: () => {
                            dispatch(push(LandingPagePaths.LandingPageCreator.replace(':name', '')))
                        }
                    }}
                />
            </EmptyStateContainer>
        )
    }

    if (isMobile) {
        return (
            <MobileTableContainer>
                <MobileTable
                    headers={mobileHeaders}
                    rows={mobileRows}
                    expandedRows={{ link: true }}
                    disableSimplePopover
                />
            </MobileTableContainer>
        )
    }

    return (
        <>
            <TableContainer>
                <CustomTable
                    headers={headers}
                    rows={rows}
                    disableFirstRowBorderTop
                    disableRowClick
                    disableRowHover
                    hideCheckboxes
                    columnsWidth={columnsWidth}
                    rowPadding={0}
                    tableCellPadding="8px 16px 8px 0"
                />
            </TableContainer>
            {open && !PreviewLoading && (
                <PreviewLandingPage
                    open={openPreview}
                    name={previewContent?.name}
                    previewContent={previewContent?.content}
                    onClose={onPreviewClose}
                />
            )}
        </>
    )
};
